/**
 * Next.js `fetch` cache tags (`next: { tags: [...] }`).
 * Use with `revalidateTag(...)` from route handlers / webhooks.
 *
 * Hierarchy is simulated by attaching multiple tags to each fetch.
 * Max tag length is 256 chars; Strapi documentIds keep us well under that.
 */

export const BLOG_CACHE_TAG = "blog";
export const BLOG_INDEX_CACHE_TAG = "blog-index";
export const META_CACHE_TAG = "meta";

const BLOG_ARTICLE_SLUG_PATTERN = /^[a-zA-Z0-9_-]+$/;

export function isBlogArticleSlug(slug: string): boolean {
  return slug.length > 0 && slug.length <= 200 && BLOG_ARTICLE_SLUG_PATTERN.test(slug);
}

export function blogArticleCacheTag(slug: string): string {
  return `blog-article:${slug}`;
}

export function blogIndexCacheTags(): string[] {
  return [BLOG_CACHE_TAG, BLOG_INDEX_CACHE_TAG];
}

export function blogArticleCacheTags(slug: string): string[] {
  return [BLOG_CACHE_TAG, blogArticleCacheTag(slug)];
}

export function parseBlogArticleCacheTag(tag: string): string | undefined {
  if (!tag.startsWith("blog-article:")) return undefined;
  const slug = tag.slice("blog-article:".length);
  return isBlogArticleSlug(slug) ? slug : undefined;
}

export type ContentResource =
  | "questions"
  | "notes"
  | "flashcards"
  | "definitions"
  | "videos"
  | "cram-sheets"
  | "topic-tests";

export type SubjectResourceKind =
  | "exam-breakdown"
  | "ia-criteria"
  | "ia-examples"
  | "command-terms";

export type ContentCacheContext = {
  subjectDocumentId: string;
  topicDocumentId: string;
};

export function metaCacheTags(): string[] {
  return [META_CACHE_TAG];
}

export function subjectResourceCacheTags(
  kind: SubjectResourceKind,
  subjectDocumentId: string,
): string[] {
  return [
    `subject:${subjectDocumentId}`,
    `subject-resource:${kind}:${subjectDocumentId}`,
  ];
}

/**
 * Build multi-tags for content fetches. Attach all relevant levels so
 * operators can purge narrowly (`questions:subtopic:…`) or broadly (`subject:…`).
 */
export function contentCacheTags(opts: {
  subjectDocumentId?: string;
  topicDocumentId?: string;
  subtopicDocumentIds?: string[];
  resource?: ContentResource;
}): string[] {
  const tags = new Set<string>();

  if (opts.subjectDocumentId) {
    tags.add(`subject:${opts.subjectDocumentId}`);
  }
  if (opts.topicDocumentId) {
    tags.add(`topic:${opts.topicDocumentId}`);
    if (opts.resource) {
      tags.add(`${opts.resource}:topic:${opts.topicDocumentId}`);
    }
  }
  for (const id of opts.subtopicDocumentIds ?? []) {
    tags.add(`subtopic:${id}`);
    if (opts.resource) {
      tags.add(`${opts.resource}:subtopic:${id}`);
    }
  }

  return Array.from(tags);
}

/** Tags allowed from the admin purge API (prefix / exact allowlist). */
const ALLOWED_EXACT_TAGS = new Set([
  BLOG_CACHE_TAG,
  BLOG_INDEX_CACHE_TAG,
  META_CACHE_TAG,
]);

const ALLOWED_TAG_PATTERNS: RegExp[] = [
  /^blog-article:[a-zA-Z0-9_-]+$/,
  /^subject:[a-zA-Z0-9_-]+$/,
  /^topic:[a-zA-Z0-9_-]+$/,
  /^subtopic:[a-zA-Z0-9_-]+$/,
  /^(questions|notes|flashcards|definitions|videos|cram-sheets|topic-tests):topic:[a-zA-Z0-9_-]+$/,
  /^(questions|notes|flashcards|definitions|videos|cram-sheets|topic-tests):subtopic:[a-zA-Z0-9_-]+$/,
  /^subject-resource:(exam-breakdown|ia-criteria|ia-examples|command-terms):[a-zA-Z0-9_-]+$/,
];

export function isAllowedCacheTag(tag: string): boolean {
  if (tag.length === 0 || tag.length > 256) return false;
  if (ALLOWED_EXACT_TAGS.has(tag)) return true;
  return ALLOWED_TAG_PATTERNS.some((re) => re.test(tag));
}
